import hljs from "highlight.js/lib/core";
import ApplicationController from "../application_component_controller";

import { fetchJSON } from "hb/lib/fetch";
import Query, { DEFAULT_QUERY } from "hb/insights/query";
import { useOutlets } from "hb/insights/use_outlets";
import { useListen } from "hb/insights/use_listen";
import { formatShort } from "hb/lib/number_helpers";
import { escapeExpression } from "hb/lib/utils";
import QueryInputHandler from "hb/insights/query_input_handler";
import { extractParameters } from "hb/insights/query_parameters";

export default class extends ApplicationController {
  static targets = [
    "progress",
    "query",
    "preview",
    "resultCounts",
    "error",
    "errorTemplate",
    "pickerSummary",
    "badgerFunctions",
    "queryWrap",
    "shadow",
    "errorHighlights",
  ];

  static values = {
    querySessionId: String,
    query: String,
    widgetId: String,
    outletDataKey: String,
    outletSelector: String,
    badgersenseMetaPath: String,
    alarmMode: Boolean,
    readonly: Boolean,
    parametersEnabled: { type: Boolean, default: false },
  };

  async connect() {
    if (this.readonlyValue) {
      this.readonlyConnect();
    } else {
      this.editConnect();
    }
  }

  editConnect() {
    useListen(this);
    useOutlets(this, {
      components: ["timePicker", "streamSelector"],
      scope: this.element,
    });
    useOutlets(this, { components: ["badgersense"] });

    fetchJSON({ path: this.badgersenseMetaPathValue, method: "GET" }).then(
      (res) => {
        this.badgersenseMeta = res;
        if (this.outletConnected) {
          this.outletComponent("badgersense").badgersenseMeta = res;
        }
      },
    );

    this.classList = this.element.classList;
    this.errorTemplate = this.compileElement(this.errorTemplateTarget);

    this.onQueryStartHandler = this.onQueryStart.bind(this);
    this.onQueryReadyHandler = this.onQueryReady.bind(this);
    this.onQueryErrorHandler = this.onQueryError.bind(this);

    this.listenWindow("queryPush", this.queryPushHandler);
    this.listenWindow("resize", this.repositionBadgersense);
    this.listenWindow("updateQueryState", this.queryStateUpdated);
    this.listen("click", this.onClickOutside, document);

    this.reloadQueryArea();

    this.listen("focus", this.onFocus, this.queryTarget);
    this.listen("blur", this.onBlur, this.queryTarget);
    this.listen("keydown", this.onKeyDown, this.queryTarget);
    this.listen("input", this.onInput, this.queryTarget);
    this.listen("click", this.onSelectionClick, this.queryTarget);

    this.listen(
      "scroll",
      () => {
        this.repositionBadgersense();
      },
      this.queryWrapTarget,
    );

    // listen for resize on queryTarget
    this.queryResizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        this.resizeQueryBox();
        this.repositionBadgersense();
      });
    });
    this.queryResizeObserver.observe(this.queryWrapTarget);

    Query.on("start", this.querySessionIdValue, this.onQueryStartHandler);
    Query.on("ready", this.querySessionIdValue, this.onQueryReadyHandler);
    Query.on("error", this.querySessionIdValue, this.onQueryErrorHandler);

    this.timezone = null;
    this.queryInputHandler = new QueryInputHandler();

    this.queryTarget.value = this.queryValue || DEFAULT_QUERY;
    this.reloadQueryArea();

    let startingQuery;

    this.queryInputHandler.addUndoState(startingQuery, 0);
    this.suggesting = false;
  }

  readonlyConnect() {
    this.resizeQueryBox();
    this.previewTarget.innerHTML = hljs.highlight(this.queryTarget.value, {
      language: "badgerql",
    }).value;
    this.element.classList.add("highlighted");
  }

  disconnect() {
    if (!this.readonlyValue) {
      this.editDisconnect();
    }
  }

  editDisconnect() {
    this.queryResizeObserver.disconnect();

    Query.off("start", this.querySessionIdValue, this.onQueryStartHandler);
    Query.off("ready", this.querySessionIdValue, this.onQueryReadyHandler);
    Query.off("error", this.querySessionIdValue, this.onQueryErrorHandler);
  }

  get badgersenseOutlet() {
    return this.outletComponent("badgersense");
  }

  hasSuggestions() {
    this.suggesting = true;
  }

  queryPushHandler({ query }) {
    const index = query.indexOf("???");
    if (index !== -1) {
      query = query.replace("???", "");
    }

    this.queryTarget.value = query;
    if (index !== -1) {
      this.queryTarget.selectionStart = this.queryTarget.selectionEnd = index;
    }
    this.reloadQueryArea();
    this.queryTarget.focus();
  }

  outletsConnected() {
    if (this.readonlyValue) return;
    if (!this.badgersenseOutlet) return;

    this.badgersenseOutlet.badgersenseMeta = this.badgersenseMeta;
    this.badgersenseOutlet.setupCallbacks({
      onSuggestionSelect: this.onSuggestionSelect.bind(this),
      positions: this.positions.bind(this),
      hasSuggestions: this.hasSuggestions.bind(this),
      onDocLinkSelect: this.docLinkSelect.bind(this),
    });
  }

  queryStateUpdated({ query }) {
    if (query !== this.queryTarget.value) {
      this.queryTarget.value = query;
      this.reloadQueryArea();
    }
  }

  onQueryStart() {
    this.errorTarget.replaceChildren();
    this.classList.remove("query-complete", "query-error");
    this.classList.add("query-running");
  }

  onQueryReady({
    detail: {
      query: { data },
    },
  }) {
    this.queryId = null;
    this.classList.remove("query-running");
    this.classList.add("query-complete");

    const { rowCount, totalCount } = data.meta;

    const parts = [`<span>${rowCount}</span>`];

    if (totalCount !== null) {
      parts.push(" of ", `<span>${formatShort(totalCount)}</span>`);
    }

    parts.push("<div>results</div>");

    this.resultCountsTarget.innerHTML = parts.join("\n");
  }

  onQueryError({
    detail: {
      query: { data },
    },
  }) {
    const { error } = data.response;

    this.queryId = null;
    this.classList.add("query-error");
    this.classList.remove("query-running");

    this.errorTarget.innerHTML = this.errorTemplate({
      error: error.message,
    });

    // Skip error position markers for parameterized queries — offsets from
    // Opticon are relative to the resolved query, not the raw query shown
    // in the editor, so they would highlight the wrong position.
    if (!this.parametersEnabledValue || extractParameters(this.queryTarget.value).length === 0) {
      if (Array.isArray(error?.range)) {
        this.highlightErrorRange(error?.range[0], error?.range[1]);
      } else if (Number.isInteger(error?.range)) {
        this.highlightErrorSpot(error?.range);
      }
    }
  }

  checkDirty() {
    if (this.queryTarget.value !== this.query) {
      this.classList.add("dirty-query");
    } else {
      this.classList.remove("dirty-query");
    }
  }

  repositionBadgersense() {
    this.outletComponent("badgersense")?.reposition();
  }

  //
  // To allow for scrolling with a syntax highlight overlay, if the textarea is
  // beyond the parent we resize the textarea on change to extend past the
  // parent div.
  //
  resizeQueryBox() {
    this.queryTarget.style.height = "auto";
    this.queryTarget.style.height = `${this.queryTarget.scrollHeight}px`;

    this.queryTarget.style.width = "auto";
    const queryScrollWidth = this.queryTarget.scrollWidth;

    if (queryScrollWidth > this.queryWrapTarget.offsetWidth) {
      this.queryTarget.style.width = `${queryScrollWidth}px`;
    } else {
      this.queryTarget.style.width = `100%`;
    }
  }

  openDocLink(evt) {
    this.docLinkSelect(evt.params.docid);
  }

  docLinkSelect(linkId) {
    window.dispatchEvent(
      new CustomEvent("doclink", {
        detail: { link: linkId, smooth: false },
      }),
    );
  }

  closeDocs() {
    window.dispatchEvent(new CustomEvent("closedocs", {}));
  }

  onSuggestionSelect(suggestion) {
    let ret = {};
    this.queryInputHandler.insertFromSuggestion(
      ret,
      {
        query: this.queryTarget.value,
        start: this.queryTarget.selectionStart,
        selection: this.badgersenseOutlet.currentSelection,
      },
      suggestion,
    );
    this.queryTarget.value = ret.query;
    this.queryTarget.selectionStart = this.queryTarget.selectionEnd =
      ret.cursorPos;
    this.queryInputHandler.addUndoState(
      ret.query,
      this.queryTarget.selectionStart,
    );
    this.reloadQueryArea();
    this.updateSuggestions(true);
    this.queryTarget.focus();
  }

  removeSuggestions() {
    this.suggesting = false;
    this.badgersenseOutlet.clear();
  }

  removeErrorHighlights() {
    this.errorHighlightsTarget.innerHTML = "";
  }

  highlightErrorRange(start, end) {
    const { value: query } = this.queryTarget;
    const begin = query.slice(0, start);
    const squiggle = query.slice(start, end);
    const rest = query.slice(end);
    this.errorHighlightsTarget.innerHTML =
      escapeExpression(begin) +
      '<span class="error-fragment">' +
      escapeExpression(squiggle) +
      "</span>" +
      escapeExpression(rest);
  }

  highlightErrorSpot(offset) {
    const { value: query } = this.queryTarget;
    const begin = query.slice(0, offset);
    const marker = query.slice(offset, offset + 1);
    const rest = query.slice(offset);
    this.errorHighlightsTarget.innerHTML =
      escapeExpression(begin) +
      '<span class="error-marker-wrap"><span class="error-marker">' +
      escapeExpression(marker) +
      "</span></span>" +
      escapeExpression(rest);
  }

  positions(offset) {
    const { value: query } = this.queryTarget;
    if (offset === 0) {
      this.shadowTarget.innerHTML =
        '<span class="shadow-offset">.</span>' + escapeExpression(query);
    } else {
      const start = query.slice(0, offset);
      const rest = query.slice(offset);
      this.shadowTarget.innerHTML =
        escapeExpression(start) +
        '<span class="shadow-offset">.</span>' +
        escapeExpression(rest);
    }

    const offsetEl = this.shadowTarget.querySelector(".shadow-offset");
    const rect = offsetEl.getBoundingClientRect();

    const { scrollLeft, offsetWidth } = this.queryWrapTarget;

    let res = {
      left: rect.left,
      bottom: rect.bottom + 5 + window.pageYOffset,
      width: offsetWidth - offsetEl.offsetLeft + scrollLeft + 35,
    };

    if (res.width < 300) {
      res.left -= 300 - res.width;
      res.width = 300;
    }

    return res;
  }

  submitQuery() {
    this.emitWindow("submitQuery", {
      query: this.queryTarget.value,
      querySessionId: this.querySessionIdValue,
    });
  }

  classifyPosition(fromInput = false) {
    const { value: query, selectionStart, selectionEnd } = this.queryTarget;
    this.removeSuggestions();

    if (selectionStart !== selectionEnd) return;

    this.repositionBadgersense();
    this.badgersenseOutlet.suggest(query, selectionStart, fromInput);
  }

  updateSuggestions(fromInput = false) {
    setTimeout(() => {
      this.classifyPosition(fromInput);
    }, 0);
  }

  onClickOutside({ target }) {
    // This prevents the dropdown from closing when you click the
    // first time
    if (this.firstInputClick === true) return;

    const inside = [this.outletElement("badgersense"), this.queryTarget].find(
      (t) => {
        return t.contains(target);
      },
    );

    if (inside) return;

    this.removeSuggestions();
    this.firstInputClick = undefined;
  }

  onBlur() {
    this.firstInputClick = undefined;
  }

  onFocus() {
    if (this.firstInputClick === undefined) {
      this.firstInputClick = true;
      this.updateSuggestions();
    }
  }

  get shouldSuggest() {
    return this.suggesting && this.badgersenseOutlet.hasSuggestions;
  }

  onSelectionClick() {
    // If we have already clicked once, don't do anything
    if (this.firstInputClick === true) {
      this.firstInputClick = false;
      return;
    }
    this.updateSuggestions(true);
  }

  onInput(evt) {
    this.queryInputHandler.addUndoState(
      evt.target.value,
      evt.target.selectionStart,
    );
    this.updateSuggestions(true);
    this.reloadQueryArea();
    this.onQueryChange();
  }

  onQueryChange() {
    this.removeErrorHighlights();
    this.emit("queryChange", { query: this.queryTarget.value });
  }

  reloadQueryArea(el = this.queryTarget) {
    this.resizeQueryBox();
    this.repositionBadgersense();
    this.checkDirty();
    this.previewTarget.innerHTML = hljs.highlight(el.value, {
      language: "badgerql",
    }).value;
    this.classList.add("highlighted");
  }

  moveSelectionUp() {
    this.badgersenseOutlet.moveSelection(-1);
  }

  moveSelectionDown() {
    this.badgersenseOutlet.moveSelection(1);
  }

  onKeyDown(event) {
    const { target, key, metaKey, ctrlKey, altKey, shiftKey } = event;
    const ret = this.queryInputHandler.handle({
      key,
      metaKey,
      ctrlKey,
      altKey,
      shiftKey,
      badgersenseMeta: this.badgersenseMeta,
      query: target.value,
      selection: this.badgersenseOutlet.currentSelection,
      // Putting in a thunk since it does some DOM stuff
      getSuggestion: () => this.badgersenseOutlet.currentSuggestion,
      isSuggesting: this.suggesting,
      start: target.selectionStart,
      end: target.selectionEnd,
    });

    if (ret === null) return;

    if (ret.preventDefault) event.preventDefault();
    if (ret.query !== undefined) target.value = ret.query;
    if (ret.cursorPos !== undefined) {
      target.selectionStart = target.selectionEnd = ret.cursorPos;
    }
    ret.calls.forEach((fn) => {
      if (Array.isArray(fn)) {
        const [name, ...args] = fn;
        this[name].call(this, ...args);
      } else {
        this[fn].call(this);
      }
    });
  }
}
